import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Cog } from 'lucide-react';
import { useMemo, useState } from 'react';

const COLORS = {
  ink: 'rgba(0,0,0,.75)',
  inkSoft: 'rgba(0,0,0,.55)',
  stroke: 'rgba(46,125,50,.20)',
  track: 'rgba(46,125,50,.12)',
  barFrom: '#9beea3ff',
  barTo: '#57B36A',
  glow: '0 12px 28px rgba(46,125,50,.08)',
};

const APS_URL = (process.env.REACT_APP_GOOGLE_SCRIPT_NOTIFY_ME_URL || '').trim();

export default function DownloadButton({ progress = 90 }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailErr, setEmailErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, ok: true, msg: '' });

  const emailRx = useMemo(() => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i, []);

  const validate = () => {
    if (!email.trim()) return false;
    if (!emailRx.test(email.trim())) {
      setEmailErr('Adresse e-mail invalide.');
      return false;
    }
    setEmailErr('');
    return true;
  };

  const submit = async () => {
    if (!validate()) {
      if (!email.trim()) setEmailErr('Veuillez renseigner votre e-mail.');
      return;
    }
    setLoading(true);
    try {
      await fetch(APS_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify({ email: email.trim(), origin: window.location.origin }),
        mode: 'no-cors',
      });
      setToast({ open: true, ok: true, msg: 'Merci ! Votre email est enregistré.' });
      setEmail('');
      setOpen(false);
    } catch (err) {
      console.error(err);
      setToast({ open: true, ok: false, msg: "Erreur lors de l'enregistrement." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          width: '100%',
          maxWidth: 720,
          mx: { xs: 2, sm: 'auto' }, // 👈 marge sur mobile
          my: 5, // espace haut/bas
          px: { xs: 3, sm: 4 },
          py: { xs: 3, sm: 4 },
          borderRadius: 4,
          border: `1px solid ${COLORS.stroke}`,
          boxShadow: COLORS.glow,
          bgcolor: 'rgba(255,255,255,.65)',
          backdropFilter: 'saturate(160%) blur(2px)',
          textAlign: 'center',
        }}
      >
        {/* Ligne titre + animation */}

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          sx={{
            mb: 2,
            '@keyframes fadeText': {
              '0%,100%': { opacity: 0.8 },
              '50%': { opacity: 1 },
            },
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        >
          <Typography
            sx={{
              color: COLORS.ink,
              fontWeight: 600,
              letterSpacing: 0.3,
              animation: 'fadeText 2.5s ease-in-out infinite',
            }}
          >
            App bientôt disponible
          </Typography>
          <Cog
            size={20}
            style={{ animation: 'spin 6s linear infinite' }} // 👈 rotation lente et fluide
            color={COLORS.inkSoft}
          />
        </Stack>

        {/* Barre de progression */}
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
          <Box sx={{ flexGrow: 1, maxWidth: { sm: '65%' } }}>
            <Box
              sx={{
                border: `1px solid ${COLORS.stroke}`,
                borderRadius: 999,
                p: 0.5,
                backgroundColor: '#fff',
              }}
            >
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 8,
                  borderRadius: 999,
                  backgroundColor: COLORS.track,
                  position: 'relative',
                  overflow: 'hidden',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 999,
                    background: `linear-gradient(
                      90deg,
                      ${COLORS.barTo} 0%,
                      ${COLORS.barFrom} 40%,
                      ${COLORS.barTo} 100%
                    )`,
                    animation: 'shimmer 7s linear infinite',
                    backgroundSize: '200% 100%',
                  },
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                }}
              />
            </Box>
          </Box>
          <Typography sx={{ color: COLORS.inkSoft, fontWeight: 600, minWidth: 38, textAlign: 'right' }}>
            {progress}%
          </Typography>
        </Stack>

        {/* CTA */}
        <Button
          onClick={() => setOpen(true)}
          startIcon={<NotificationsActiveIcon />}
          sx={{
            mt: 3,
            borderRadius: 999,
            px: 2.5,
            height: 42,
            textTransform: 'none',
            fontWeight: 700,
            color: COLORS.ink,
            border: `1px solid ${COLORS.stroke}`,
            background: 'linear-gradient(180deg, rgba(255,255,255,.9), rgba(255,255,255,.7))',
            boxShadow: '0 6px 18px rgba(0,0,0,.06)',
            '&:hover': {
              background: 'linear-gradient(180deg, rgba(255,255,255,.95), rgba(255,255,255,.8))',
              transform: 'translateY(-1px)',
            },
          }}
        >
          Me prévenir
        </Button>
      </Box>

      {/* Dialog email */}
      <Dialog open={open} onClose={() => !loading && setOpen(false)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Être notifié(e) du lancement</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2, color: COLORS.inkSoft }}>
            Laissez votre e-mail : nous vous écrirons dès que l’application sera disponible.
          </Typography>
          <TextField
            fullWidth
            type="email"
            label="Votre e-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={validate}
            error={!!emailErr}
            helperText={emailErr || ' '}
            autoFocus
            inputProps={{ inputMode: 'email' }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)} disabled={loading} variant="text">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={loading}
            variant="contained"
            sx={{ bgcolor: '#2E7D32', '&:hover': { bgcolor: '#25682a' } }}
          >
            {loading ? 'Envoi…' : 'Valider'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Feedback */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3400}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.ok ? 'success' : 'error'}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{ width: '100%' }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}